import {
    Component,
    forwardRef,
    inject,
    input,
    OnChanges,
    OnDestroy,
    output,
    SimpleChanges
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import {
    AbstractControl,
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { ExternalFirm, ProfileRepCode, UserByAdvisor } from '@trade-platform/ui-shared';
import { ProfileStoreFacade } from '@advisor-ui/app-services';
import {
    AixDataTestingDirective,
    AixDropdownComponent,
    AixNextPendingFieldDirective,
    AixRadioGroupComponent
} from '@trade-platform/ui-components';

@Component({
    selector: 'aix-advisor-firm-information',
    templateUrl: './advisor-firm-information.html',
    styleUrls: ['./advisor-firm-information.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AixAdvisorFirmInformationGroupComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: AixAdvisorFirmInformationGroupComponent,
            multi: true
        }
    ],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AixDropdownComponent,
        AixDataTestingDirective,
        AixRadioGroupComponent,
        AixNextPendingFieldDirective
    ]
})
export class AixAdvisorFirmInformationGroupComponent
    implements ControlValueAccessor, OnChanges, OnDestroy
{
    private _fb = inject(UntypedFormBuilder);
    profileStore = inject(ProfileStoreFacade);

    startOrder = input<any>();
    firms = input<ExternalFirm[]>([]);
    advisors = input<UserByAdvisor[]>([]);

    onSelectRepCode = output<string>();
    onClearRepCode = output();
    onClearAdvisor = output();
    loadTrainingData = output();

    form: UntypedFormGroup;

    externalFirmId: number | null;
    advisor: string | null;
    advisorTypes = [
        {
            value: 'brokerDealer',
            title: 'Broker Dealer'
        },
        {
            value: 'registeredInvestmentAdvisor',
            title: 'Registered Investment Advisor'
        }
    ];
    repCode: string | null;

    subscriptions: Subscription[] = [];

    private _onChange: (value: any | null | undefined) => void;
    private _destroy$: Subject<void> = new Subject<void>();

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this._createFormGroup();
        this._setupObservables();
    }

    selectFirm(value: ExternalFirm) {
        if (value) {
            this.externalFirmId = value.id;
            if (value.type === 'RIA') {
                this.form.patchValue({ firmType: 'registeredInvestmentAdvisor' });
            } else if (
                value.type === 'Broker Dealer' ||
                value.type === 'N/A' ||
                value.type === 'Insurance'
            ) {
                this.form.patchValue({ firmType: 'brokerDealer' });
            }
        }
    }

    selectRepCode(value: ProfileRepCode) {
        if (value) {
            if (value.id && this.repCode !== value.id) {
                this.advisor = null;
                this.form.patchValue({ advisor: '' });
            }
            this.onSelectRepCode.emit(value.id);
        } else {
            this.onClearRepCode.emit();
        }
    }

    selectAdvisor(value: UserByAdvisor) {
        const repCode = this.form.get('repCode')?.value;
        if (value && repCode?.length) {
            this.repCode = repCode[0]?.id as string;
            setTimeout(() => {
                this.loadTrainingData.emit();
            }, 100);
        } else {
            this.onClearAdvisor.emit();
        }
    }

    firmLabelStrategy({ name, crdNumber, type }: ExternalFirm) {
        if (crdNumber && crdNumber !== '') {
            return `${crdNumber} - ${name} - ${type}`;
        } else {
            return `${name} - ${type}`;
        }
    }

    repCodeLabelStrategy(repCode: ProfileRepCode) {
        return `${repCode.fullName} - ${repCode.repCode}`;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.firms && this.firms().length === 1 && !this.externalFirmId) {
            this.form.patchValue({ firm: this.firms() });
        }
        if (
            changes.startOrder &&
            this.startOrder() &&
            this.startOrder().repCodes.length === 1 &&
            !this.repCode
        ) {
            this.repCode = this.startOrder().repCodes[0].id;
            this.form.patchValue({ repCode: this.startOrder().repCodes });
        }
        if (changes.advisors && this.advisors().length === 1 && !this.advisor) {
            this.form.patchValue({ advisor: this.advisors() });
        }
    }

    patchForm() {
        const data = {};

        this.form.patchValue(data);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());

        if (this._destroy$ && !this._destroy$.closed) {
            this._destroy$.next();
            this._destroy$.complete();
        }
    }

    writeValue(value: any | null | undefined): void {
        if (!value) {
            return;
        }
    }

    validate(control: AbstractControl) {
        return this.form.valid ? null : { invalid: true };
    }

    registerOnChange(fn: (value: any | null | undefined) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // TODO: implement this method
        // throw new Error('registerOnTouched not implemented');
    }

    private _createFormGroup() {
        this.form = this._fb.group({
            firmType: this._fb.control('', [Validators.required]),
            firm: this._fb.control('', [Validators.required]),
            repCode: this._fb.control('', [Validators.required]),
            advisor: this._fb.control('', [Validators.required])
        });
    }

    private _setupObservables() {
        this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(value => {
            if (this._onChange) {
                this._onChange(value);
            }
        });
    }
}
